/**
 * Auto-categorization. Ported from `BudgetStore.swift`.
 *
 * User-defined sorting rules win over the built-in merchant list, so teaching
 * the app once permanently overrides a wrong guess.
 */

import { BudgetItem, SortingRule, Transaction } from './types';

/**
 * Empty and one-character keywords match everything — `"hello".includes("")` is
 * true — so a rule that short would swallow the whole ledger.
 */
export const MIN_SORTING_KEYWORD_LENGTH = 2;

export class InvalidKeywordError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidKeywordError';
  }
}

export function keywordMatches(keyword: string, description: string): boolean {
  const normalized = keyword.trim().toLowerCase();
  if (normalized.length < MIN_SORTING_KEYWORD_LENGTH) return false;
  return description.toLowerCase().includes(normalized);
}

export function normalizeSortingKeyword(keyword: string): string {
  const normalized = keyword.trim().toLowerCase();
  if (normalized.length < MIN_SORTING_KEYWORD_LENGTH) {
    throw new InvalidKeywordError(
      `Keyword must be at least ${MIN_SORTING_KEYWORD_LENGTH} characters`
    );
  }
  return normalized;
}

interface BuiltInRule {
  keywords: readonly string[];
  /** Matched against `BudgetItem.name`, so renaming an envelope breaks the rule. */
  budgetItemName: string;
}

/**
 * Built-in merchant keywords, evaluated in order — first match wins.
 *
 * Gas comes before groceries so "Kroger Fuel" is a fill-up, not a shop.
 */
export const BUILT_IN_RULES: readonly BuiltInRule[] = [
  {
    budgetItemName: 'Gas',
    keywords: [
      'cenex', 'shell ', 'chevron', 'exxon', 'mobil ', 'marathon', 'murphy', 'casey',
      'pilot ', 'flying j', 'buc-ee', 'quiktrip', 'racetrac', 'kum & go', 'wawa',
      'sheetz', 'speedway', 'kwik', 'circle k', 'sinclair', 'conoco', 'phillips 66',
      'valero', 'citgo', 'texaco', 'sunoco', 'amack', 'kroger fuel', ' fuel ',
      "love's", 'loves #',
    ],
  },
  {
    budgetItemName: 'Groceries',
    keywords: [
      'harris teeter', 'kroger', 'publix', 'aldi ', 'lidl ', 'trader joe',
      'whole foods', 'food lion', 'safeway', 'wm supercenter', 'walmart',
      "sam's club", 'sams club', 'costco', 'target', 'heb ', 'winco', 'meijer',
      'piggly', 'wegman', 'giant eagle', 'shoprite', 'stop & shop',
      'fresh market', 'food city', 'commissary',
    ],
  },
  {
    budgetItemName: 'Eating Out',
    keywords: [
      'taco bell', 'chick-fil-a', 'chickfila', 'mcdonald', 'chipotle', 'starbucks',
      'subway', 'wendy', 'burger king', 'arby', 'popeye', 'sonic drive',
      'dunkin', 'panera', 'panda express', 'domino', 'papa john', 'pizza hut',
      "chili's", 'chilis', 'applebee', 'olive garden', 'ihop', 'denny',
      'waffle house', 'sawa sushi', 'cracker barrel', 'zaxby', 'cookout',
      'five guys', 'shake shack', 'whataburger', 'raising cane', 'wingstop',
      'firehouse sub', 'jimmy john', 'jersey mike', 'el pollo', 'culver',
      'jack in the box', 'in-n-out', 'del taco', 'little caesars', 'kfc',
      'tropical smoothie', 'smoothie king', 'creambrook', '24-7 travel store',
      'noodles & co', 'noodles and co', 'buffalo wild', 'cheddar', 'longhorn',
      'outback', 'red lobster', 'red robin', 'texas roadhouse', 'bob evans',
      'golden corral', 'crumbl', 'insomnia cookie', 'nothing bundt',
    ],
  },
  { budgetItemName: 'Dental', keywords: ['partners in denta', 'dental', 'dentist'] },
  {
    budgetItemName: 'Hair Cuts',
    keywords: [
      'great clips', 'supercuts', 'sport clips', 'fantastic sam',
      'hair cuttery', 'cost cutters', 'barber',
    ],
  },
  {
    budgetItemName: 'Animals',
    keywords: [
      'tractor supply', 'petsmart', 'petco', 'pet supplies', 'chewy.com',
      'veterinar', 'vet clinic', 'animal hospital',
    ],
  },
  { budgetItemName: 'Supplements', keywords: ['proadjuster', 'chiropract'] },
  { budgetItemName: 'YMCA', keywords: ['ymca'] },
];

export interface CategorySuggestion {
  budgetItemId: string;
  /** `rule` came from something the user taught; `builtIn` is a guess we shipped. */
  source: 'rule' | 'builtIn';
  matchedKeyword: string;
}

/**
 * The best guess for a description, or `null` when nothing is confident enough.
 *
 * Only active budget items are eligible — suggesting an archived envelope would
 * file the charge somewhere the user is no longer looking. `items` is filtered
 * here rather than trusted to arrive pre-filtered, since a rule can outlive the
 * envelope it points at.
 */
export function suggestCategory(
  description: string,
  sortingRules: readonly SortingRule[],
  items: readonly BudgetItem[]
): CategorySuggestion | null {
  const haystack = description.toLowerCase();
  const eligible = items.filter((item) => item.isActive);
  const itemIds = new Set(eligible.map((item) => item.id));

  for (const rule of sortingRules) {
    if (!rule.isActive) continue;
    if (!keywordMatches(rule.keyword, haystack)) continue;
    if (!itemIds.has(rule.budgetItemId)) continue;
    return { budgetItemId: rule.budgetItemId, source: 'rule', matchedKeyword: rule.keyword };
  }

  for (const rule of BUILT_IN_RULES) {
    for (const keyword of rule.keywords) {
      if (!haystack.includes(keyword)) continue;
      // "kroger" must not claim "kroger fuel" — the gas rule above owns that.
      if (keyword === 'kroger' && haystack.includes('fuel')) continue;
      const item = eligible.find((candidate) => candidate.name === rule.budgetItemName);
      if (item) {
        return { budgetItemId: item.id, source: 'builtIn', matchedKeyword: keyword };
      }
    }
  }

  return null;
}

/**
 * Suggestions for a batch of transactions.
 *
 * Refunds are skipped: where money coming back should land is a judgement call,
 * and guessing wrong silently credits the wrong envelope.
 */
export function suggestCategories(
  transactions: readonly Transaction[],
  sortingRules: readonly SortingRule[],
  items: readonly BudgetItem[]
): Map<string, CategorySuggestion> {
  const suggestions = new Map<string, CategorySuggestion>();
  for (const transaction of transactions) {
    if (transaction.amount > 0) continue;
    const suggestion = suggestCategory(transaction.description, sortingRules, items);
    if (suggestion) suggestions.set(transaction.id, suggestion);
  }
  return suggestions;
}

const CHASE_PREFIXES = [
  'POS DEBIT', 'ACH DEBIT', 'ACH CREDIT', 'MISC_DEBIT',
  'DEBIT CRD', 'CREDIT CRD', 'ONLINE TRANSFER',
];

/**
 * A sensible starting keyword when the user creates a rule from a transaction.
 *
 * Strips the bank's prefixes and trailing store numbers so "POS DEBIT HARRIS
 * TEETER #221 CARY NC" offers "harris teeter" rather than the whole line.
 */
export function extractMerchantKeyword(description: string): string {
  let text = description.replace(/\s+/g, ' ').trim();

  for (const prefix of CHASE_PREFIXES) {
    if (text.toUpperCase().startsWith(prefix)) {
      text = text.slice(prefix.length).trim();
    }
  }

  text = text.replace(/^(SQ|TST|SP|PAYPAL|WWP)\s*\*\s*/i, '');
  // Store numbers, phone numbers, dates, and trailing codes are not merchants.
  text = text.replace(/#\s*\d+/g, ' ');
  text = text.replace(/\b\d{3}-\d{3}-?\d{0,4}\b/g, ' ');
  text = text.replace(/\b\d{1,2}\/\d{1,2}(\/\d{2,4})?\b/g, ' ');
  text = text.replace(/\b\d{3,}\b/g, ' ');

  const words = text
    .split(/\s+/)
    .filter(Boolean)
    .filter((word) => /[A-Za-z]/.test(word));

  // Two words is usually the merchant ("harris teeter", "taco bell") without
  // dragging in the city that follows it.
  const keyword = words.slice(0, 2).join(' ').toLowerCase().trim();
  return keyword.length >= MIN_SORTING_KEYWORD_LENGTH ? keyword : '';
}
